import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from './Dashboard';
import {
  Export, NotionLogo, Cards as TrelloIcon, CheckSquare,
  Sparkle, Calendar, ArrowSquareOut, ChartBar, FunnelSimple, EnvelopeSimple,
} from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const INTEGRATION_META = {
  notion:  { label: 'Notion',  icon: NotionLogo,  color: '#E11D48' },
  trello:  { label: 'Trello',  icon: TrelloIcon,  color: '#0079BF' },
  clickup: { label: 'ClickUp', icon: CheckSquare, color: '#7B68EE' },
};

const SHAPE_META = {
  branding:            'Branding',
  product_blueprint:   'Product Blueprint',
  auto_build:          'Auto-Build',
  opportunity_scan:    'Opportunity Scan',
  competitor_analysis: 'Competitor Analysis',
};

const SHAPE_FILTERS = [['', 'Tudo'], ...Object.entries(SHAPE_META)];

const StatCard = ({ icon: Icon, label, value, hint, accent = '#2563EB', testId }) => (
  <div
    className="p-5 bg-[#0F0F0F] border border-white/10 rounded-2xl"
    data-testid={testId}
    style={{ borderColor: `${accent}33` }}
  >
    <div className="flex items-center justify-between mb-3">
      <span className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-semibold">{label}</span>
      <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${accent}22` }}>
        <Icon size={14} weight="bold" style={{ color: accent }} />
      </div>
    </div>
    <div className="text-3xl font-[Outfit] font-bold" style={{ color: accent }}>{value}</div>
    {hint && <p className="text-xs text-white/50 mt-1">{hint}</p>}
  </div>
);

const formatRelativeTime = (iso) => {
  if (!iso) return '—';
  const date = new Date(iso);
  const diffMs = Date.now() - date.getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'agora mesmo';
  if (mins < 60) return `há ${mins} min`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `há ${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `há ${days}d`;
  return date.toLocaleDateString('pt-PT');
};

const Exports = ({ user }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [stats, setStats] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [integration, setIntegration] = useState('all');
  const [shapeType, setShapeType] = useState('');
  const [sendingDigest, setSendingDigest] = useState(false);

  const loadStats = useCallback(async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/exports/stats`, { withCredentials: true });
      setStats(data);
    } catch {
      setStats(null);
    }
  }, []);

  const loadHistory = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ integration, limit: '100' });
      if (shapeType) params.set('shape_type', shapeType);
      const { data } = await axios.get(`${BACKEND_URL}/api/exports/history?${params.toString()}`, { withCredentials: true });
      setItems(data.items || []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [integration, shapeType]);

  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => { loadHistory(); }, [loadHistory]);

  const handleSendDigest = useCallback(async () => {
    setSendingDigest(true);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/exports/digest/send`, {}, { withCredentials: true });
      if (data.sent) {
        toast.success('Digest enviado!', {
          description: `${data.week_total} ${data.week_total === 1 ? 'export' : 'exports'} resumidos · enviado para ${data.to}`,
          duration: 6000,
        });
      } else {
        toast.message('Digest preparado, mas o envio falhou — verifica o RESEND_API_KEY.');
      }
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      if (status === 412) {
        toast.message(detail || 'Sem exports esta semana — nada para resumir.');
      } else {
        toast.error(detail || 'Falha a enviar digest.');
      }
    } finally {
      setSendingDigest(false);
    }
  }, []);

  const topIntegrationLabel = useMemo(() => {
    if (!stats?.top_integration) return '—';
    return INTEGRATION_META[stats.top_integration]?.label || stats.top_integration;
  }, [stats]);

  return (
    <DashboardLayout user={user} activePage="/exports">
      <div className="space-y-8" data-testid="exports-page">
        {/* Hero */}
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-3xl font-[Outfit] font-bold tracking-tight flex items-center gap-3">
              <Export size={28} weight="bold" className="text-[#2563EB]" />
              {t('exports.title')}
            </h1>
            <p className="text-white/55 text-sm mt-1.5">
              {t('exports.subtitle')}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              data-testid="btn-send-digest"
              onClick={handleSendDigest}
              disabled={sendingDigest || (stats?.total_this_month ?? 0) === 0}
              className="px-4 py-2 bg-[#A855F7]/15 hover:bg-[#A855F7]/25 border border-[#A855F7]/40 text-[#A855F7] rounded-lg text-sm font-medium inline-flex items-center gap-1.5 transition disabled:opacity-40 disabled:cursor-not-allowed"
              title={(stats?.total_this_month ?? 0) === 0 ? 'Sem exports recentes — gera conteúdo primeiro' : 'Envia o resumo dos últimos 7 dias para o teu email'}
            >
              <EnvelopeSimple size={13} weight="bold" />
              {sendingDigest ? t('exports.sending_digest') : t('exports.send_digest')}
            </button>
            <button
              data-testid="btn-manage-integrations"
              onClick={() => navigate('/settings#notion')}
              className="px-4 py-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg text-sm font-medium inline-flex items-center gap-1.5 transition"
            >
              {t('exports.manage_integrations')} <ArrowSquareOut size={12} weight="bold" />
            </button>
          </div>
        </div>

        {/* Stat cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            icon={Export}
            label={t('exports.stat_total')}
            value={stats?.total_all_time ?? '—'}
            hint={stats?.last_export_at ? `${t('common.more')}: ${formatRelativeTime(stats.last_export_at)}` : t('exports.no_exports_empty')}
            accent="#2563EB"
            testId="stat-total"
          />
          <StatCard
            icon={Calendar}
            label={t('exports.stat_month')}
            value={stats?.total_this_month ?? '—'}
            hint={stats?.total_all_time ? `${Math.round((stats.total_this_month / stats.total_all_time) * 100)}%` : null}
            accent="#10B981"
            testId="stat-month"
          />
          <StatCard
            icon={Sparkle}
            label={t('exports.stat_top_shape')}
            value={stats?.top_shape ? (SHAPE_META[stats.top_shape] || stats.top_shape) : '—'}
            hint={stats?.top_shape_count ? `${stats.top_shape_count}` : null}
            accent="#A855F7"
            testId="stat-top-shape"
          />
          <StatCard
            icon={ChartBar}
            label={t('exports.stat_top_integration')}
            value={topIntegrationLabel}
            hint={stats?.per_integration ? (
              Object.entries(stats.per_integration).map(([k, v]) => `${INTEGRATION_META[k]?.label || k}: ${v}`).join(' · ')
            ) : null}
            accent="#F59E0B"
            testId="stat-top-integration"
          />
        </div>

        {/* Filters */}
        <div className="flex items-center gap-3 flex-wrap" data-testid="exports-filters">
          <div className="inline-flex items-center gap-1 text-xs uppercase tracking-[0.2em] text-white/40 font-semibold">
            <FunnelSimple size={12} weight="bold" /> {t('exports.filter_label')}
          </div>
          {['all', 'notion', 'trello', 'clickup'].map((key) => {
            const isActive = integration === key;
            const intMeta = INTEGRATION_META[key];
            const IntIcon = intMeta?.icon;
            return (
              <button
                key={key}
                data-testid={`filter-int-${key}`}
                onClick={() => setIntegration(key)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition border ${
                  isActive
                    ? 'bg-white/10 border-white/20 text-white'
                    : 'bg-transparent border-white/10 text-white/55 hover:text-white hover:bg-white/5'
                }`}
                style={isActive && intMeta ? { borderColor: intMeta.color, color: intMeta.color } : undefined}
              >
                {intMeta ? <><IntIcon size={11} weight="fill" className="inline mr-1" />{intMeta.label}</> : 'Todos'}
              </button>
            );
          })}
          <span className="text-white/20 mx-1">·</span>
          <select
            data-testid="filter-shape"
            value={shapeType}
            onChange={(e) => setShapeType(e.target.value)}
            className="px-3 py-1.5 bg-[#0A0A0A] border border-white/10 rounded-lg text-xs text-white/80 focus:outline-none focus:border-white/30"
          >
            {SHAPE_FILTERS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>

        {/* Timeline */}
        <div className="space-y-2" data-testid="exports-timeline">
          {loading ? (
            <div className="text-center py-12 text-white/40 text-sm">{t('common.loading')}</div>
          ) : items.length === 0 ? (
            <div className="text-center py-16 bg-[#0F0F0F] border border-white/10 border-dashed rounded-2xl">
              <Export size={32} weight="bold" className="text-white/20 mx-auto mb-3" />
              <p className="text-white/55 text-sm">{t('exports.no_exports_empty')}{integration !== 'all' ? ` (${INTEGRATION_META[integration]?.label})` : ''}</p>
              <p className="text-white/35 text-xs mt-1">
                {t('exports.no_exports_hint')}
              </p>
            </div>
          ) : (
            items.map((row, idx) => {
              const meta = INTEGRATION_META[row.integration] || {};
              const Icon = meta.icon || Export;
              return (
                <div
                  key={`${row.integration}-${row.external_id || idx}`}
                  data-testid={`export-row-${row.integration}`}
                  className="flex items-center gap-4 p-4 bg-[#0F0F0F] hover:bg-[#141414] border border-white/10 rounded-xl transition group"
                >
                  <div
                    className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ background: `${meta.color}22` }}
                  >
                    <Icon size={16} weight="fill" style={{ color: meta.color }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-white truncate">{row.title || 'Untitled'}</div>
                    <div className="text-xs text-white/45 mt-0.5 flex items-center gap-2 flex-wrap">
                      <span style={{ color: meta.color }}>{meta.label}</span>
                      <span className="text-white/20">·</span>
                      <span>{SHAPE_META[row.shape_type] || row.shape_type}</span>
                      {row.checklist_count > 0 && (
                        <>
                          <span className="text-white/20">·</span>
                          <span className="text-[#10B981]">{row.checklist_count} action items</span>
                        </>
                      )}
                      <span className="text-white/20">·</span>
                      <span>{formatRelativeTime(row.created_at)}</span>
                    </div>
                  </div>
                  {row.url && (
                    <a
                      href={row.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`export-open-${row.integration}`}
                      className="opacity-0 group-hover:opacity-100 px-3 py-1.5 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg text-xs font-medium transition inline-flex items-center gap-1.5 flex-shrink-0"
                    >
                      {t('exports.open_external')} <ArrowSquareOut size={11} weight="bold" />
                    </a>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Exports;
